/**
 * @fileoverview Phases Component
 * 
 * Displays session phases (onboarding, room scan, waiting, check-in, testing) as
 * visual annotations on the video timeline. Fetches phase data from the API and
 * renders them as colored range annotations that show when each phase occurred
 * during the session recording.
 * 
 * **Features:**
 * - Fetches phase data asynchronously from API
 * - Calculates phase timing relative to recording start
 * - Renders phases as colored timeline annotations
 * - Shows phase names on hover with tooltips
 * - Supports main phases and sub-phases with different visual heights
 * - Automatically updates when journey step changes
 * 
 * **Phase Types:**
 * - **Onboarding** (secondary color) - Initial setup and instructions
 * - **Room Scan** (brand color, sub-phase) - Environment verification
 * - **Waiting For Proctor** (brand color, sub-phase) - Queue time
 * - **Check-in** (brand color, sub-phase) - Identity verification
 * - **Testing** (success color) - Active exam period
 * 
 * @module SessionViewer/Player/Controls/Annotations/Phases
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import { getStore, useExamSessionStore } from '../../../../../stores/StoreContext';
import RangeAnnotation from '../RangeAnnotation';
import { View, Tooltip } from '@smarterservices/smarter-ui';

/**
 * Styles for the horizontal separator line below phase annotations
 */
const hrStyle = {
    position: 'relative',
    top: '3px',
    border: 'none',
    height: '1px',
    backgroundColor: '#CCC',
    marginTop: '5px',
    zIndex: 10,
};

/**
 * Color mapping for different phase types
 * Maps phase identifiers to SmarterUI color variants
 */
const PHASE_COLORS = {
    onboarding: 'secondary',
    roomScan: 'brand',
    WaitingForProctor: 'brand',
    checkin: 'brand',
    testing: 'success'
};

/**
 * Visual configuration for phase rendering
 */
const PHASE_CONFIG = {
    mainPhase: {
        rowHeight: 8,
        verticalOffset: 7
    },
    subPhase: {
        rowHeight: 4,
        verticalOffset: 9
    }
};

/**
 * Phases Component
 * 
 * Renders session phases as timeline annotations showing the progression
 * through onboarding, check-in, and testing stages.
 * 
 * @component
 * @returns {JSX.Element} Phase annotations with tooltips
 * 
 * @example
 * // Used in the timeline controls
 * <Phases />
 */
const Phases = () => {
    // State for phase data and annotations
    const [phases, setPhases] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Tooltip state
    const [tooltipContent, setTooltipContent] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipTarget, setTooltipTarget] = useState(null);

    // Store references
    const ExamSessionStore = getStore('examSessionStore');
    const AppStore = getStore('appStore');

    // Subscribe to journey changes to refresh phases
    const currentJourneyStepId = useExamSessionStore((state) => state.journey?.currentJourneyStepId);

    /**
     * Fetch and process session phases
     * Runs when component mounts or when journey step changes
     */
    useEffect(() => {
        let isMounted = true;

        const fetchPhases = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Fetch phase data from API
                const response = await AppStore.ApiClient.getSessionPhases({
                    installSid: ExamSessionStore.installSid,
                    examSid: ExamSessionStore.examSid,
                    sessionSid: ExamSessionStore.sessionSid,
                });

                if (!response.successful) {
                    throw new Error(response.error || 'Failed to fetch session phases');
                }

                // Only update state if component is still mounted
                if (!isMounted) return;

                const rawPhases = response.data;

                // Validate recording start date
                const recordingStartDate = ExamSessionStore.sessionData?.recordingStartDate;
                if (!recordingStartDate) {
                    console.warn('[Phases] No recording start date available, cannot calculate phase timing');
                    setPhases([]);
                    return;
                }

                // Parse recording start date as UTC
                const recordingStart = moment.utc(recordingStartDate);

                // Transform API phase data into annotation format
                const processedPhases = rawPhases.map((phase) => {
                    // Parse phase start date as UTC
                    const phaseStartDate = moment.utc(phase.startTime);

                    // Calculate seconds offset from recording start
                    // Clamp to 0 to prevent negative values (phases before recording)
                    let start = Math.max(0, phaseStartDate.diff(recordingStart, 'seconds'));

                    // Calculate duration in seconds if end date exists
                    let duration = 0;
                    if (phase.endTime) {
                        const phaseEndDate = moment.utc(phase.endTime);
                        duration = phaseEndDate.diff(phaseStartDate, 'seconds');
                    }

                    // Determine visual configuration based on phase type
                    const config = phase.isSubPhase ? PHASE_CONFIG.subPhase : PHASE_CONFIG.mainPhase;

                    return {
                        phase: phase.phase,
                        name: phase.name,
                        range: {
                            start: phase.startTime,
                            end: phase.endTime
                        },
                        score: 0,
                        color: PHASE_COLORS[phase.phase] || 'brand',
                        sessionSid: ExamSessionStore.sessionSid,
                        sid: phase.phase,
                        rowHeight: config.rowHeight,
                        verticalOffset: config.verticalOffset,
                        duration,
                        start
                    };
                });

                setPhases(processedPhases);
            } catch (err) {
                console.error('[Phases] Error fetching session phases:', err);
                if (isMounted) {
                    setError(err.message);
                    setPhases([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchPhases();

        // Cleanup function to prevent state updates after unmount
        return () => {
            isMounted = false;
        };
    }, [currentJourneyStepId, ExamSessionStore.installSid, ExamSessionStore.examSid, ExamSessionStore.sessionSid, ExamSessionStore.sessionData?.recordingStartDate, AppStore.ApiClient]);

    /**
     * Handle mouse enter on phase annotation
     * Shows tooltip with phase name
     */
    const handleAnnotationMouseEnter = useCallback((e, annotation) => {
        let content = "";
        if (annotation.phase === 'testing') {
            content = "Testing";
        } else if (annotation.phase === 'onboarding') {
            content = "Onboarding";
        } else if (annotation.phase === 'checkin') {
            content = "Check-in";
        } else if (annotation.phase === 'waitingForProctor') {
            content = "Waiting for proctor";
        }

        setTooltipContent(content);
        setTooltipTarget(e.currentTarget);
        setShowTooltip(true);
    }, []);

    /**
     * Handle mouse leave on phase annotation
     * Hides tooltip
     */
    const handleAnnotationMouseLeave = useCallback(() => {
        setShowTooltip(false);
        setTooltipTarget(null);
    }, []);

    /**
     * Memoize phase annotation components
     * Only re-render when phases array changes
     */
    const annotationComponents = useMemo(() => {
        return phases.map((phase) => (
            <RangeAnnotation
                key={`annotation_${phase.phase}`}
                annotation={phase}
                type={{ type: 'phases' }}
                row={0}
                totalRows={1}
                rowHeight={phase.rowHeight}
                verticalOffset={phase.verticalOffset}
                onMouseEnter={handleAnnotationMouseEnter}
                onMouseLeave={handleAnnotationMouseLeave}
            />
        ));
    }, [phases, handleAnnotationMouseEnter, handleAnnotationMouseLeave]);

    // Don't render anything while loading or if there's an error
    if (isLoading || error) {
        return null;
    }

    // Don't render if no phases available
    if (phases.length === 0) {
        return null;
    }

    return (
        <>
            <View
                as="div"
                margin="x-small none none none"
                withVisualDebug={false}
            >
                {annotationComponents}
                <hr style={hrStyle} />
            </View>
            {tooltipTarget && (
                <Tooltip
                    renderTip={tooltipContent}
                    isShowingContent={showTooltip}
                    placement="top"
                    constrain="none"
                    positionTarget={tooltipTarget}
                    themeOverride={{
                        fontSize: '0.7rem',
                        padding: '0.5rem'
                    }}
                    styles={{
                        zIndex: 99999,
                        width: '300px',
                    }}
                />
            )}
        </>
    );
};

Phases.displayName = 'Phases';

export default Phases;
